// components/seller/EditProductModal.tsx
import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  Upload,
  AlertCircle,
  Package,
  Layers,
  Image as ImageIcon,
  DollarSign,
  Box,
  ChevronRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface Variant {
  id?: string;
  title: string;
  unit: string;
  value: string;
  price: number;
  mrp: number;
  stock: number;
}

interface EditProductModalProps {
  product: any;
  onClose: () => void;
  onUpdate: (productId: string, formData: FormData) => Promise<void>;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  product,
  onClose,
  onUpdate,
}) => {
  const [name, setName] = useState(product.name || "");
  const [description, setDescription] = useState(product.description || "");
  const [categoryId, setCategoryId] = useState(
    product.categoryId || product.category?.id || "",
  );
  const [variants, setVariants] = useState<Variant[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"basic" | "variants" | "images">(
    "basic",
  );

  // Initialize variants when product changes
  useEffect(() => {
    if (product.variants && product.variants?.length > 0) {
      const initializedVariants = product.variants.map((v: any) => ({
        id: v.id,
        title: v.title,
        unit: v.unit,
        value: v.value?.toString() || "",
        price: v.price,
        mrp: v.mrp || v.price || 0, // Ensure MRP is properly set
        stock: v.inventory?.stock || v.stock || 0,
      }));
      setVariants(initializedVariants);
    } else {
      setVariants([]);
    }
  }, [product]);

  // Initialize images when product changes
  useEffect(() => {
    if (product.images && product.images?.length > 0) {
      const previews = product.images
        .map((img: any) => {
          if (typeof img === "string") return img;
          if (img.url) return img.url;
          if (img.image?.data) {
            try {
              const buffer = img.image.data;
              const base64String = btoa(
                new Uint8Array(buffer)?.reduce(
                  (data, byte) => data + String.fromCharCode(byte),
                  "",
                ),
              );
              return `data:image/jpeg;base64,${base64String}`;
            } catch {
              return "";
            }
          }
          return "";
        })
        .filter((url: string) => url);
      setImagePreviews(previews);
    } else {
      setImagePreviews([]);
    }
  }, [product]);

  const addVariant = () =>
    setVariants([
      ...variants,
      { title: "", unit: "kg", value: "", price: 0, mrp: 0, stock: 0 },
    ]);

  const removeVariant = (index: number) =>
    setVariants(variants.filter((_, i) => i !== index));

  const updateVariant = (index: number, field: keyof Variant, value: any) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };

    // Optional: Auto-set MRP if price is higher than MRP
    if (field === "price" && updated[index].mrp < value) {
      updated[index].mrp = value;
    }

    setVariants(updated);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImages([...images, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        setImagePreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    if (index < images?.length) setImages(images.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    if (!name.trim()) {
      setFormError("Product name is required");
      setActiveTab("basic");
      return false;
    }
    if (!description.trim()) {
      setFormError("Description is required");
      setActiveTab("basic");
      return false;
    }
    for (let i = 0; i < variants?.length; i++) {
      const v = variants[i];
      if (!v.title.trim()) {
        setFormError(`Variant ${i + 1}: title required`);
        setActiveTab("variants");
        return false;
      }
      if (!v.value.trim()) {
        setFormError(`Variant ${i + 1}: value required`);
        setActiveTab("variants");
        return false;
      }
      if (v.price <= 0) {
        setFormError(`Variant ${i + 1}: price must be > 0`);
        setActiveTab("variants");
        return false;
      }
      if (v.mrp <= 0) {
        setFormError(`Variant ${i + 1}: MRP must be > 0`);
        setActiveTab("variants");
        return false;
      }
      if (v.mrp < v.price) {
        setFormError(`Variant ${i + 1}: MRP cannot be less than price`);
        setActiveTab("variants");
        return false;
      }
      if (v.stock < 0) {
        setFormError(`Variant ${i + 1}: stock can't be negative`);
        setActiveTab("variants");
        return false;
      }
    }
    setFormError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setFormError("");
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("description", description.trim());
    formData.append("categoryId", categoryId.trim());

    const variantsData = variants.map((v) => {
      const d: any = {
        title: v.title.trim(),
        unit: v.unit,
        value: v.value.trim(),
        price: Number(v.price),
        mrp: Number(v.mrp), // Make sure MRP is included
        stock: Number(v.stock),
      };
      if (v.id) d.id = v.id;
      return d;
    });

    formData.append("variants", JSON.stringify(variantsData));
    images.forEach((img) => formData.append("images", img));

    try {
      await onUpdate(product.id, formData);
      onClose();
    } catch (err: any) {
      setFormError(err.message || "Failed to update product");
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: "basic", label: "Info", icon: Package },
    {
      id: "variants",
      label: "Variants",
      icon: Layers,
      badge: variants?.length,
    },
    {
      id: "images",
      label: "Images",
      icon: ImageIcon,
      badge: imagePreviews?.length,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "rgba(5, 20, 10, 0.75)",
        backdropFilter: "blur(12px)",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.93, opacity: 0, y: 16 }}
        transition={{ type: "spring", damping: 28, stiffness: 380 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full overflow-hidden"
        style={{
          maxWidth: "460px",
          borderRadius: "20px",
          background: "#ffffff",
          boxShadow:
            "0 32px 80px rgba(0,0,0,0.28), 0 0 0 1px rgba(16,185,129,0.12)",
        }}
      >
        {/* Thin accent bar at top */}
        <div
          style={{
            height: "3px",
            background: "linear-gradient(90deg, #10b981, #0d9488, #0891b2)",
          }}
        />

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div>
            <h2
              style={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "-0.3px",
              }}
            >
              Edit Product
            </h2>
            <p style={{ fontSize: "11px", color: "#94a3b8", marginTop: "1px" }}>
              {product.name || "Update product details"}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "#f1f5f9",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                "#fee2e2";
              (e.currentTarget as HTMLButtonElement).style.color = "#ef4444";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                "#f1f5f9";
              (e.currentTarget as HTMLButtonElement).style.color = "#64748b";
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pb-2">
          {tabs.map(({ id, label, icon: Icon, badge }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  padding: "6px 4px",
                  borderRadius: "9px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: active ? 600 : 500,
                  background: active
                    ? "linear-gradient(135deg, #ecfdf5, #f0fdfa)"
                    : "transparent",
                  color: active ? "#059669" : "#94a3b8",
                  boxShadow: active ? "0 0 0 1.5px #a7f3d0" : "none",
                  transition: "all 0.15s",
                }}
              >
                <Icon size={12} />
                {label}
                {badge !== undefined && badge > 0 && (
                  <span
                    style={{
                      background: active ? "#10b981" : "#cbd5e1",
                      color: "white",
                      borderRadius: "20px",
                      padding: "0 5px",
                      fontSize: "9px",
                      fontWeight: 700,
                      lineHeight: "16px",
                    }}
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div
          style={{ height: "1px", background: "#f1f5f9", margin: "0 20px" }}
        />

        <form onSubmit={handleSubmit}>
          {/* Error */}
          <AnimatePresence>
            {formError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                style={{
                  margin: "8px 20px 0",
                  borderRadius: "10px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  padding: "8px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <AlertCircle
                  size={13}
                  style={{ color: "#ef4444", flexShrink: 0 }}
                />
                <p style={{ fontSize: "11px", color: "#dc2626" }}>
                  {formError}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tab Content */}
          <div
            style={{
              padding: "12px 20px",
              maxHeight: "340px",
              overflowY: "auto",
            }}
          >
            <AnimatePresence mode="wait">
              {/* BASIC TAB */}
              {activeTab === "basic" && (
                <motion.div
                  key="basic"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#475569",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Product Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Organic Basmati Rice"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        fontSize: "13px",
                        color: "#0f172a",
                        outline: "none",
                        boxSizing: "border-box",
                        background: "#f8fafc",
                        transition: "border-color 0.15s",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#10b981")}
                      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#475569",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Description <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      placeholder="Describe your product..."
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        fontSize: "13px",
                        color: "#0f172a",
                        outline: "none",
                        resize: "none",
                        boxSizing: "border-box",
                        background: "#f8fafc",
                        transition: "border-color 0.15s",
                        fontFamily: "inherit",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#10b981")}
                      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                    />
                  </div>
                </motion.div>
              )}

              {/* VARIANTS TAB */}
              {activeTab === "variants" && (
                <motion.div
                  key="variants"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.15 }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "8px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={addVariant}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "5px 10px",
                        borderRadius: "8px",
                        border: "none",
                        color: "white",
                        fontSize: "11px",
                        fontWeight: 600,
                        cursor: "pointer",
                        background: "#064e3b",
                      }}
                    >
                      <Plus size={12} /> Add Variant
                    </button>
                  </div>

                  {variants.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "28px 0",
                        borderRadius: "12px",
                        background: "#f8fafc",
                        border: "1.5px dashed #e2e8f0",
                      }}
                    >
                      <Package
                        size={28}
                        style={{ color: "#cbd5e1", margin: "0 auto 8px" }}
                      />
                      <p style={{ fontSize: "12px", color: "#94a3b8" }}>
                        No variants yet
                      </p>
                      <button
                        type="button"
                        onClick={addVariant}
                        style={{
                          marginTop: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#064e3b",
                        }}
                      >
                        + Add first variant
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <AnimatePresence>
                        {variants.map((variant, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            style={{
                              background: "#f8fafc",
                              borderRadius: "12px",
                              border: "1.5px solid #e2e8f0",
                              padding: "10px 12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "6px",
                                    background:
                                      "linear-gradient(135deg, #10b981, #0d9488)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "white",
                                      fontSize: "9px",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {index + 1}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "#374151",
                                  }}
                                >
                                  Variant {index + 1}
                                </span>
                                {variant.id && (
                                  <span
                                    style={{
                                      fontSize: "9px",
                                      background: "#dbeafe",
                                      color: "#2563eb",
                                      padding: "1px 5px",
                                      borderRadius: "4px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    saved
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeVariant(index)}
                                style={{
                                  padding: "3px",
                                  borderRadius: "5px",
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  color: "#f87171",
                                }}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: "6px",
                                marginBottom: "6px",
                              }}
                            >
                              {[
                                {
                                  label: "Title",
                                  field: "title",
                                  placeholder: "1 KG",
                                  type: "text",
                                },
                                {
                                  label: "Value",
                                  field: "value",
                                  placeholder: "1",
                                  type: "text",
                                },
                              ].map(({ label, field, placeholder, type }) => (
                                <div key={field}>
                                  <label
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: 600,
                                      color: "#6b7280",
                                      display: "block",
                                      marginBottom: "3px",
                                    }}
                                  >
                                    {label}
                                  </label>
                                  <input
                                    type={type}
                                    placeholder={placeholder}
                                    value={(variant as any)[field]}
                                    onChange={(e) =>
                                      updateVariant(
                                        index,
                                        field as keyof Variant,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "5px 8px",
                                      borderRadius: "7px",
                                      border: "1.5px solid #e2e8f0",
                                      fontSize: "12px",
                                      color: "#0f172a",
                                      outline: "none",
                                      background: "white",
                                      boxSizing: "border-box",
                                    }}
                                    onFocus={(e) =>
                                      (e.target.style.borderColor = "#10b981")
                                    }
                                    onBlur={(e) =>
                                      (e.target.style.borderColor = "#e2e8f0")
                                    }
                                  />
                                </div>
                              ))}
                              <div>
                                <label
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    display: "block",
                                    marginBottom: "3px",
                                  }}
                                >
                                  Unit
                                </label>
                                <select
                                  value={variant.unit}
                                  onChange={(e) =>
                                    updateVariant(index, "unit", e.target.value)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "5px 8px",
                                    borderRadius: "7px",
                                    border: "1.5px solid #e2e8f0",
                                    fontSize: "12px",
                                    color: "#0f172a",
                                    outline: "none",
                                    background: "white",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <option value="kg">kg</option>
                                  <option value="g">g</option>
                                  <option value="l">L</option>
                                  <option value="ml">ml</option>
                                  <option value="pcs">pcs</option>
                                </select>
                              </div>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "6px",
                              }}
                            >
                              <div>
                                <label
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    display: "block",
                                    marginBottom: "3px",
                                  }}
                                >
                                  Price (₹)
                                </label>
                                <div style={{ position: "relative" }}>
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: "8px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      fontSize: "11px",
                                      color: "#10b981",
                                      fontWeight: 700,
                                    }}
                                  >
                                    ₹
                                  </span>
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={variant.price}
                                    onChange={(e) =>
                                      updateVariant(
                                        index,
                                        "price",
                                        parseFloat(e.target.value) || 0,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "5px 8px 5px 20px",
                                      borderRadius: "7px",
                                      border: "1.5px solid #e2e8f0",
                                      fontSize: "12px",
                                      color: "#0f172a",
                                      outline: "none",
                                      background: "white",
                                      boxSizing: "border-box",
                                    }}
                                    onFocus={(e) =>
                                      (e.target.style.borderColor = "#10b981")
                                    }
                                    onBlur={(e) =>
                                      (e.target.style.borderColor = "#e2e8f0")
                                    }
                                  />
                                </div>
                              </div>

                              {/* MRP */}
                              <div>
                                <label
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    display: "block",
                                    marginBottom: "3px",
                                  }}
                                >
                                  MRP (₹)
                                </label>
                                <div style={{ position: "relative" }}>
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: "8px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      fontSize: "11px",
                                      color: "#10b981",
                                      fontWeight: 700,
                                    }}
                                  >
                                    ₹
                                  </span>
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={variant.mrp}
                                    onChange={(e) =>
                                      updateVariant(
                                        index,
                                        "mrp",
                                        parseFloat(e.target.value) || 0,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "5px 8px 5px 20px",
                                      borderRadius: "7px",
                                      border: "1.5px solid #e2e8f0",
                                      fontSize: "12px",
                                      color: "#0f172a",
                                      outline: "none",
                                      background: "white",
                                      boxSizing: "border-box",
                                    }}
                                    onFocus={(e) =>
                                      (e.target.style.borderColor = "#10b981")
                                    }
                                    onBlur={(e) =>
                                      (e.target.style.borderColor = "#e2e8f0")
                                    }
                                  />
                                </div>
                              </div>

                              <div>
                                <label
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    display: "block",
                                    marginBottom: "3px",
                                  }}
                                >
                                  Stock
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={variant.stock}
                                  onChange={(e) =>
                                    updateVariant(
                                      index,
                                      "stock",
                                      parseInt(e.target.value) || 0,
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "5px 8px",
                                    borderRadius: "7px",
                                    border: "1.5px solid #e2e8f0",
                                    fontSize: "12px",
                                    color: "#0f172a",
                                    outline: "none",
                                    background: "white",
                                    boxSizing: "border-box",
                                  }}
                                  onFocus={(e) =>
                                    (e.target.style.borderColor = "#10b981")
                                  }
                                  onBlur={(e) =>
                                    (e.target.style.borderColor = "#e2e8f0")
                                  }
                                />
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </motion.div>
              )}

              {/* IMAGES TAB */}
              {activeTab === "images" && (
                <motion.div
                  key="images"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.15 }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "8px",
                    }}
                  >
                    {imagePreviews.map((preview, index) => (
                      <motion.div
                        key={index}
                        initial={{ scale: 0.85, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        style={{
                          position: "relative",
                          aspectRatio: "1",
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1.5px solid #e2e8f0",
                        }}
                        className="group"
                      >
                        <img
                          src={preview}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          style={{
                            position: "absolute",
                            top: "3px",
                            right: "3px",
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            background: "#ef4444",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <X size={10} />
                        </button>
                      </motion.div>
                    ))}

                    <label
                      style={{
                        aspectRatio: "1",
                        borderRadius: "10px",
                        border: "1.5px dashed #cbd5e1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        background: "#f8fafc",
                        gap: "4px",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        (
                          e.currentTarget as HTMLLabelElement
                        ).style.borderColor = "#10b981";
                        (e.currentTarget as HTMLLabelElement).style.background =
                          "#ecfdf5";
                      }}
                      onMouseLeave={(e) => {
                        (
                          e.currentTarget as HTMLLabelElement
                        ).style.borderColor = "#cbd5e1";
                        (e.currentTarget as HTMLLabelElement).style.background =
                          "#f8fafc";
                      }}
                    >
                      <Upload size={16} style={{ color: "#94a3b8" }} />
                      <span
                        style={{
                          fontSize: "9px",
                          color: "#94a3b8",
                          fontWeight: 600,
                        }}
                      >
                        Add
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>

                  <p
                    style={{
                      marginTop: "10px",
                      fontSize: "10px",
                      color: "#94a3b8",
                      background: "#f8fafc",
                      padding: "6px 10px",
                      borderRadius: "8px",
                    }}
                  >
                    💡 Existing images are kept. New uploads will be added.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "10px 20px 16px",
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              gap: "8px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: "9px",
                borderRadius: "11px",
                border: "1.5px solid #e2e8f0",
                background: "white",
                color: "#64748b",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "#f8fafc";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "white";
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 2,
                padding: "9px",
                borderRadius: "11px",
                border: "none",
                background: isSubmitting
                  ? "#a7f3d0"
                  : "linear-gradient(135deg, #10b981, #0d9488)",
                color: "white",
                fontSize: "12px",
                fontWeight: 700,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                transition: "all 0.15s",
                boxShadow: isSubmitting
                  ? "none"
                  : "0 4px 12px rgba(16,185,129,0.35)",
              }}
            >
              {isSubmitting ? (
                <>
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      border: "2px solid rgba(255,255,255,0.5)",
                      borderTopColor: "white",
                      borderRadius: "50%",
                      animation: "spin 0.6s linear infinite",
                    }}
                  />
                  Saving...
                </>
              ) : (
                <>
                  Update Product
                  <ChevronRight size={14} />
                </>
              )}
            </button>
          </div>
        </form>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </motion.div>
    </motion.div>
  );
};

export default EditProductModal;
